import { useSettings, useSettingsManager } from "@app-composables/settings";
import useDebugDialog, { debugDialogIsOpen } from "@app-composables/use-debug-dialog";
import useAppStore from "@app-store/app";
import EventsManager from "@app-utilities/events-manager";
import { modalFatal } from "@app-utilities/modals";
import { AppInsightsEvent } from "@app-utilities/observability-events";
import { type CommonProps, useObservability, useObservabilityManager } from "@qamf/observability";
import { defineImplementation } from "@qamf/shell-app-sdk";
import { debounce } from "lodash-es";
import { RestError } from "scarlett";

import { defineCloudBackend } from "./rest";
import Router from "./router";

let settingsEnsured = false;
export async function ensureSetupSettings() {
	if (settingsEnsured) return;

	const settingsManager = useSettingsManager();

	await settingsManager.setup({
		launchModeResolver() {
			return Promise.resolve(__LAUNCH_MODE__);
		},
		versionResolver() {
			return Promise.resolve(__VERSION__);
		},
		async settingsResolver() {
			const environment = __ENVIRONMENT__;
			const channel = __CHANNEL__;

			const contextFileUrl = __LAUNCH_MODE__ === "Standard"
				? "/context.json"
				: `/settings/${environment}-${channel}.json`;

			const response = await fetch(contextFileUrl, { method: "GET", cache: "no-cache" })
				.then((data) => data.json())
				.catch((error) => {
					console.error(error);
				});

			return response;
		}
	});
	settingsEnsured = true;
}

export async function setupObservability() {
	const { appInsightsConnString, launchMode, channel, version, amplitudeProjectId, mixPanelProjectId } = useSettings();
	const { setupAppInsights, setupMixPanel, setupAmplitude, setCommonPropsResolver, setEventsPrefix } = useObservabilityManager();

	if (!appInsightsConnString.value || !launchMode.value || !channel.value || !version.value) throw new Error("Missing data in app settings file ");

	setEventsPrefix("qubicaamf.bowler");

	await Promise.all([
		setupAppInsights({
				config: {
				connectionString: appInsightsConnString.value,
				maxAjaxCallsPerView: -1,
				disableExceptionTracking: true,
				disableTelemetry: launchMode.value === "DevelopSession"
			}
		}),
		mixPanelProjectId.value ? setupMixPanel(mixPanelProjectId.value) : Promise.resolve(),
		amplitudeProjectId.value ? setupAmplitude(amplitudeProjectId.value) : Promise.resolve(),
	]);

	setCommonPropsResolver(function commonObservabilityProps(): CommonProps {
		const route = Router.currentRoute;
		const { environment, channel } = useSettings();

		const mapValue = <T extends string, U extends string>(value: T, mapping: Record<T, U>): U => mapping[value] ?? (value as unknown as U);
		const channelMap = {
			beta: "Beta",
			stable: "Stable"
		} as const;
		const mappedChannel = mapValue(channel.value as keyof typeof channelMap, channelMap);

		const environmentMap = {
			production: "Production",
			staging: "Staging",
			development: "Development",
			expo: "Expo"
		} as const;
		const mappedEnvironment = mapValue(environment.value as keyof typeof environmentMap, environmentMap);


		const [companyId, systemId] = location.pathname.split("/").filter(Boolean);

		const commonProperties: CommonProps = {
			Environment: mappedEnvironment,
			Channel: mappedChannel,
			Version: version.value as string,
			Source: "Bowler",
			CenterId: Number(systemId),
			CompanyId: Number(companyId),
			RoutePath: route.value?.fullPath ?? "",
			RouteName: route.value?.name?.toString() ?? "",
			LaunchMode: launchMode.value,
			IsPwaMode: window.matchMedia('(display-mode: standalone)').matches
		};

		return commonProperties;
	});
}

function initErrorHandling() {
	const fatalErrors: any[] = [];
	let fatalErrorLocation: string | null = null;
	const logErrors = debounce(() => {
		const printErrorEl = (el: any, title?: string) => {
			console.groupCollapsed("\x1b[31m%s\x1b[0m", title ?? el.error.message);
			console.info(el.datetime);
			console.error(el.error);
			if (el.extras)
				console.info(el.extras);
			console.groupEnd();
		};
		if (fatalErrors.length === 1)
			printErrorEl(fatalErrors[0], "A fatal error occurred");
		else {
			console.groupCollapsed("\x1b[31m%s\x1b[0m", `Intercepted fatal errors: ${fatalErrors.length}`);
			fatalErrors.forEach(er => printErrorEl(er));
			console.groupEnd();
		}
	}, 1000);
	let _modalFatalShown = false;

	const { trackException } = useObservability();

	EventsManager.onError(async(error, extras) => {
		if (!fatalErrorLocation)
			fatalErrorLocation = window.location.href;

		trackException(error, extras);
		fatalErrors.push({ error, extras, datetime: new Date() });
		logErrors();

		// Skip the fatal-error modal (and the forced reload below) while the dev settings modal
		// is open — the user is actively fixing a bad custom backend URL, and interrupting them
		// with a full-page reload makes that impossible.
		if (_modalFatalShown || debugDialogIsOpen.value) return;
		const { trackEvent } = useObservability();

		trackEvent(AppInsightsEvent.FatalError, {
			description: "Unhandled exception feedback to user",
			error,
			isRestError: error instanceof RestError,
			errorName: error.name,
			errorStack: error.stack,
			errorMessage: error.message
		});

		_modalFatalShown = true;
		useAppStore().setLoadingView(false);
		await modalFatal();

		// If the user opened the dev settings modal while the fatal modal was showing, skip the
		// forced reload on close too — same reasoning as the early return above: don't wipe out
		// an in-progress backend URL fix. _modalFatalShown resets so a later real error still shows.
		if (debugDialogIsOpen.value) {
			_modalFatalShown = false;
			return;
		}

		if (fatalErrorLocation) {
			location.assign(fatalErrorLocation);
			fatalErrorLocation = null;
		}
	});
	window.onerror = (message, source, lineno, colno, error) => {
		if (!error) return;

		EventsManager.emitError(error, { message, source, lineno, colno });
		return true;
	};
	window.onunhandledrejection = (event: PromiseRejectionEvent) => {
		event.preventDefault();
		if (event.reason && (event.reason instanceof DOMException || event.reason instanceof Error || event.reason instanceof RestError))
			EventsManager.emitError(event.reason);
		else
			EventsManager.emitError(new Error("UnhandledPromiseRejection"), { ...event });
	};
}

export default async function boot() {
	await ensureSetupSettings();
	useDebugDialog().exposeOpenDevSettings();

	const {appInsightsConnString, bowlerUrl, bowlerUrlBeta} = useSettings();

	if(!appInsightsConnString.value || !bowlerUrl.value || !bowlerUrlBeta.value) throw new Error("Missing data in app settings file");

	defineImplementation("useCloudBackend", defineCloudBackend());
	await setupObservability();
	initErrorHandling();
}
